import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

//Components
import { Button, Form, Icons, Select, SidePanel, SidePanelBody, SidePanelFooter, SidePanelHeader, Switch, Tooltip } from '@gpstrackit/gtc-react-controls';
import { NotificationManager } from "Components/Notifications";
import { bulkCreateDriverPerformanceCasesFull, openModalCreatePanel, reset } from "Modules/driverPerformance/views/cases/modals/createPanel/redux/slice";

import './style.scss';
import { convertToKPH } from "Core/data/Helpers";

const CreatePanel = () => {
    const dispatch = useDispatch();
    const { messages, locale } = useIntl();
    const user = useSelector((state) => state.securityRedux.user);
    const { loading, open, loadingCreate, error } = useSelector(state => state.driverPerformanceCasesTabCreatePanelRedux || {});
    const [data, setData] = useState({ includeHighlight: false });
    const [enableSend, setEnableSend] = useState({});
    const [milesOptions, setMilesOptions] = useState([]);
    const prevLoadingCreateRef = useRef(false);
    const formRef = Form.useForm();

    const RISK_SCORE_OPTIONS = [
        { id: 10 }, { id: 20 }, { id: 30 }, { id: 40 }, { id: 50 },
        { id: 60 }, { id: 70 }, { id: 80 }, { id: 90 }
    ];
    const LAST_COACHED_OPTIONS = [
        { id: 0 }, { id: 7 }, { id: 15 }, { id: 30 }, { id: 60 }
    ];
    const SEVERITY_SCORE_OPTIONS = [
        { id: 10 }, { id: 20 }, { id: 30 }, { id: 40 }, { id: 50 },
        { id: 60 }, { id: 70 }, { id: 80 }, { id: 90 }
    ];
    const FREQUENCY_OPTIONS = [
        { id: 1, label: '1' }, { id: 2, label: '2' }, { id: 3, label: '3' },
        { id: 4, label: '4' }, { id: 5, label: '5' },
        { id: -99, label: messages['driver_performance_cases_all'] }
    ];
    const MAX_EVENTS_OPTIONS = [
        { id: 1, label: '1' }, { id: 2, label: '2' }, { id: 3, label: '3' },
        { id: 4, label: '4' }, { id: 5, label: '5' },
        { id: -99, label: messages['driver_performance_cases_all'] }
    ];

    useEffect(() => {
        if (user) {
            setMilesOptions([
                convertToKph(50),
                convertToKph(100),
                convertToKph(150),
                convertToKph(200),
                { id: -99, label: 'NA' }
            ]);
        }
    }, [user, locale])

    useEffect(() => {
        if (open) {
            setData({ includeQueueForCoaching: false })
        }
    }, [open])

    useEffect(() => {
        if (data) {
            setEnableSend(validateFields());
        }
    }, [data])

    useEffect(() => {
        return () => {
            dispatch(openModalCreatePanel({ open: false }));
        }
    }, [])


    useEffect(() => {
        const prevLoadingCreate = prevLoadingCreateRef.current;
        // Only notify when an update cycle finished: loadingCreate true -> false
        if (prevLoadingCreate && !loadingCreate) {
            if (error) {
                NotificationManager.error(
                    error?.error || messages['errorMessage'],
                    messages['driver_performance_cases_create_saveError'],
                    3000,
                    null,
                    null,
                    'error',
                );
            } else {
                NotificationManager.success(
                    messages['success'],
                    messages['driver_performance_cases_create_saveSuccessfully'],
                    3000,
                    null,
                    null,
                    'success',
                );
            }
        }
        prevLoadingCreateRef.current = loadingCreate;
    }, [loadingCreate, error])


    const convertToKph = (value) => {
        return { id: value, label: user.isKph ? `${convertToKPH(value)} ${messages['kilometers']}` : `${value} ${messages['miles']}` }
    }

    const validateFields = () => {
        const validateFields = ['milesDriven', 'lastCoached', 'byFrequency', 'maxEventsPerType'];
        return validateFields.every(field => data[field]);
    }

    const handleSave = () => {
        if (data) {
            const payload = {
                driverScope: data.milesDriven?.id,
                limitedBy: data.lastCoached?.id,
                eventCategories: data.byFrequency?.id,
                eventsLimit: data.maxEventsPerType?.id,
                includeHighlight: data.includeQueueForCoaching,

                // TODO: We will leave these 2 properties disabled for the next scope
                // riskScore: data.riskScore?.id,
                // severityScore: data.severityScore?.id,
            }
            dispatch(bulkCreateDriverPerformanceCasesFull(payload));
        }
        dispatch(openModalCreatePanel({ open: false }));
    }

    const handleCancel = () => {
        dispatch(openModalCreatePanel({ open: false }));
    }

    return (
        <SidePanel isOpen={open} onToggle={handleCancel} hiddenButton={true} style={{ width: '350px', transform: 'translateX(350px)' }}>
            <SidePanelHeader>
                <label className="create-casesTap-panel__title">{messages['driver_performance_cases_create_createCases']}</label>
            </SidePanelHeader>
            <SidePanelBody>
                <Form
                    ref={formRef.ref}
                    loading={loading}
                    onValidSubmit={() => {
                        if (validateFields()) {
                            handleSave();
                        }
                    }}
                    onChangeValues={() => { }}
                    className="cases"
                >
                    {/* Driver Scope Section */}
                    <div className="create-panel-section">
                        <h4 className="create-panel-section-header">{messages['driver_performance_cases_create_driverScope']}</h4>

                        <div className="create-panel-field">
                            <strong className={'create-casesTab-info-strong'}>
                                {user.isKph ? messages['driver_performance_cases_create_kilometersDriven'] : messages['driver_performance_cases_create_milesDriven']}
                                <span className="text-danger create-casesTab-info-strong-required"> *</span>
                                <Tooltip
                                    className="create-panel-tooltip"
                                    placement="bottom"
                                    title={messages['driver_performance_cases_create_milesDrivenDetail']}>
                                    <Icons name="help" size="small" />
                                </Tooltip>
                            </strong>
                            <Select
                                id="milesDriven"
                                value={data.milesDriven}
                                onChange={(value) => setData({ ...data, milesDriven: value })}
                                options={milesOptions}
                                textField="label"
                                valueField="id"
                                placeholder={messages['driver_performance_cases_create_select']}
                                loading={loading}
                            />
                        </div>

                        <div className="create-panel-field vjs-hidden">
                            <strong className={'create-casesTab-info-strong'}>
                                {messages['driver_performance_cases_create_riskScore']}
                                <Tooltip
                                    className="create-panel-tooltip"
                                    placement="bottom"
                                    title={messages['driver_performance_cases_create_riskScoreDetail']}>
                                    <Icons name="help" size="small" />
                                </Tooltip>
                            </strong>
                            <Select
                                id="riskScore"
                                value={data.riskScore}
                                onChange={(value) => setData({ ...data, riskScore: value })}
                                options={RISK_SCORE_OPTIONS}
                                textField="id"
                                valueField="id"
                                placeholder={messages['driver_performance_cases_create_select']}
                                loading={loading}
                            />
                        </div>
                    </div>
                    {/* Limited By Section */}
                    <div className="create-panel-section">
                        <h4 className="create-panel-section-header">{messages['driver_performance_cases_create_limitedBy']}</h4>

                        <div className="create-panel-field">
                            <strong className={'create-casesTab-info-strong'}>
                                {messages['driver_performance_cases_create_lastCoached']}
                                <span className="text-danger create-casesTab-info-strong-required"> *</span>
                                <Tooltip
                                    className="create-panel-tooltip"
                                    placement="bottom"
                                    title={messages['driver_performance_cases_create_lastCoachedDetail']}>
                                    <Icons name="help" size="small" />
                                </Tooltip>
                            </strong>
                            <Select
                                id="lastCoached"
                                value={data.lastCoached}
                                onChange={(value) => setData({ ...data, lastCoached: value })}
                                options={LAST_COACHED_OPTIONS}
                                textField="id"
                                valueField="id"
                                placeholder={messages['driver_performance_cases_create_select']}
                                loading={loading}
                            />
                        </div>
                    </div>
                    {/* Event Types to Include Section */}
                    <div className="create-panel-section">
                        <h4 className="create-panel-section-header">{messages['driver_performance_cases_create_eventTypesToInclude']}</h4>

                        <div className="create-panel-switch-container">
                            <strong className={'create-casesTab-info-strong'}>
                                {messages['driver_performance_cases_create_eventStatusQueueForCoaching']}
                            </strong>
                            <Tooltip
                                className="create-panel-tooltip-switch"
                                placement="right"
                                title={messages['driver_performance_cases_create_eventStatusQueueForCoachingDetail']}>
                                <Icons name="help" size="small" />
                            </Tooltip>

                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <Switch
                                checked={data?.includeQueueForCoaching || false}
                                id="includeQueueForCoaching"
                                labelPosition="top"
                                name="includeQueueForCoaching"
                                onChange={(value) => setData({ ...data, includeQueueForCoaching: value })}
                                size="small"
                            />
                        </div>

                        <div className="create-panel-field vjs-hidden">
                            <strong className={'create-casesTab-info-strong'}>
                                {messages['driver_performance_cases_create_severityScore']}
                                <Tooltip
                                    className="create-panel-tooltip"
                                    placement="bottom"
                                    title={messages['driver_performance_cases_create_severityScoreDetail']}>
                                    <Icons name="help" size="small" />
                                </Tooltip>
                            </strong>
                            <Select
                                id="severityScore"
                                value={data.severityScore}
                                onChange={(value) => setData({ ...data, severityScore: value })}
                                options={SEVERITY_SCORE_OPTIONS}
                                textField="id"
                                valueField="id"
                                placeholder={messages['driver_performance_cases_create_select']}
                                loading={loading}
                            />
                        </div>

                        <div className="create-panel-field">
                            <strong className={'create-casesTab-info-strong'}>
                                {messages['driver_performance_cases_create_byFrequency']}
                                <span className="text-danger create-casesTab-info-strong-required"> *</span>
                                <Tooltip
                                    className="create-panel-tooltip"
                                    placement="bottom"
                                    title={messages['driver_performance_cases_create_byFrequencyDetail']}>
                                    <Icons name="help" size="small" />
                                </Tooltip>
                            </strong>
                            <Select
                                id="byFrequency"
                                value={data.byFrequency}
                                onChange={(value) => setData({ ...data, byFrequency: value })}
                                options={FREQUENCY_OPTIONS}
                                textField="label"
                                valueField="id"
                                placeholder={messages['driver_performance_cases_create_select']}
                                loading={loading}
                            />
                        </div>

                        <div className="create-panel-note">
                            {messages['driver_performance_cases_create_noteEventsCaped']}
                        </div>
                    </div>
                    {/* Limit Examples Section */}
                    <div className="create-panel-section">
                        <h4 className="create-panel-section-header">{messages['driver_performance_cases_create_limitExamples']}</h4>

                        <div className="create-panel-field">
                            <strong className={'create-casesTab-info-strong'}>
                                {messages['driver_performance_cases_create_maximumEventsPerEventType']}
                                <span className="text-danger create-casesTab-info-strong-required"> *</span>
                                <Tooltip
                                    className="create-panel-tooltip"
                                    placement="bottom"
                                    title={messages['driver_performance_cases_create_maximumEventsPerEventTypeDetail']}>
                                    <Icons name="help" size="small" />
                                </Tooltip>
                            </strong>
                            <Select
                                id="maxEventsPerType"
                                value={data.maxEventsPerType}
                                onChange={(value) => setData({ ...data, maxEventsPerType: value })}
                                options={MAX_EVENTS_OPTIONS}
                                textField="label"
                                valueField="id"
                                placeholder={messages['driver_performance_cases_create_select']}
                                loading={loading}
                            />
                        </div>
                    </div>
                    <div className="create-panel-note-final">
                        <strong>{messages['driver_performance_cases_create_note']}: </strong>
                        <span>{messages['driver_performance_cases_create_eventNote']}</span>
                    </div>
                </Form>
            </SidePanelBody>
            <SidePanelFooter>
                <div className="create-panel-footer">
                    <Button
                        isDisabled={loading}
                        onClick={() => dispatch(reset({ open: false }))}
                        variant="secondary">
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loading || !enableSend}
                        onClick={() => formRef?.submit()}
                        variant="primary">
                        {messages["driver_performance_cases_create_send"]}
                    </Button>
                </div>
            </SidePanelFooter>
        </SidePanel>
    )
}

export default CreatePanel
